import { useMemo } from 'react';

import { styled } from '@mui/material';
import { Grid, IconButtonGhost, Typography } from '@kc-console/common-ui';
import { useTranslation } from '@kc-console/core';
import IcChevronLeftSmallRegular from '@kep/kcds-icons/IcChevronLeftSmallRegular';
import IcChevronRightSmallRegular from '@kep/kcds-icons/IcChevronRightSmallRegular';

import { usePickerConfig } from './PickerConfigContext';

import type { ReactDatePickerCustomHeaderProps } from 'react-datepicker';
import type { DatePickerLocale, PeriodPickerKind } from '../types';

interface PeriodPickerHeaderProps extends ReactDatePickerCustomHeaderProps {
  calendarView: PeriodPickerKind;
  locale: DatePickerLocale;
}

/** 기간 단위 native view의 페이지 이동과 제목을 표시하는 전용 header */
export default function PeriodPickerHeader({
  calendarView,
  date,
  decreaseYear,
  increaseYear,
  nextYearButtonDisabled,
  prevYearButtonDisabled,
  visibleYearsRange,
}: PeriodPickerHeaderProps) {
  const { t } = useTranslation();
  const periodPickerDisabled = usePickerConfig()?.disabled ?? false;
  const title = useMemo(() => {
    if (calendarView === 'year' && visibleYearsRange) {
      return `${visibleYearsRange.startYear} - ${visibleYearsRange.endYear}`;
    }

    return String(date.getFullYear());
  }, [calendarView, date, visibleYearsRange]);
  const previousAriaLabel = t('ui:datepicker.period.action.previous_year');
  const nextAriaLabel = t('ui:datepicker.period.action.next_year');

  return (
    <PeriodPickerHeaderRoot container alignItems="center">
      <NavigationContainer>
        <NavigationButton
          aria-label={previousAriaLabel}
          disabled={periodPickerDisabled || prevYearButtonDisabled}
          onClick={decreaseYear}
        >
          <IcChevronLeftSmallRegular />
        </NavigationButton>
        <CenteredTypography variant="label/medium/lg">{title}</CenteredTypography>
        <NavigationButton
          aria-label={nextAriaLabel}
          disabled={periodPickerDisabled || nextYearButtonDisabled}
          onClick={increaseYear}
        >
          <IcChevronRightSmallRegular />
        </NavigationButton>
      </NavigationContainer>
    </PeriodPickerHeaderRoot>
  );
}

const PeriodPickerHeaderRoot = styled(Grid)`
  width: 100%;
`;

const NavigationContainer = styled('div')`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
`;

const NavigationButton = styled(IconButtonGhost)`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
`;

const CenteredTypography = styled(Typography)`
  flex: 1;
  text-align: center;
`;
